import type OLMap from 'ol/Map';
import type VectorLayerType from 'ol/layer/Vector';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import RegularShape from 'ol/style/RegularShape';
import { storeToRefs } from 'pinia';
import { watch } from 'vue';
import { getDragPan, getDragZoom } from 'shared/map/domain/interactions';
import { getRenderedEventCoordinate } from 'shared/map/domain/coordinate';
import {
  getShapeBasis,
  geographicFromLocal,
  localDeltaFromGeographic,
  localFromGeographic,
  type ShapeBasis,
} from 'shared/map/domain/placeGeometry';
import { metersToMillimeters, millimetersToMeters } from 'shared/common/domain/units';
import { useShapeStore } from 'shared/shapes/stores/shapeStore';
import { useSelectedPackageStorageStore } from '../stores/selectedPackageStorageStore';
import type { StoragePlace } from '../stores/selectedPackageStorageStore';
import { useSelectedStorageAreaStore } from '../stores/selectedStorageAreaStore';
import { useSelectedShapeStore } from '../stores/selectedShapeStore';

// Minimum place size in millimeters (50 mm).
const minPlaceSize = 50;

// Minimum pointer travel (pixels) before a press becomes a move/resize, so a
// simple click never nudges a place.
const dragThresholdPx = 4;

const handleStyle = new Style({
  image: new RegularShape({
    points: 4,
    radius: 6,
    angle: Math.PI / 4,
    fill: new Fill({ color: '#ffffff' }),
    stroke: new Stroke({ color: '#2563eb', width: 2 }),
  }),
});

/** Local corners of a place rectangle, ordered TL, TR, BR, BL. */
function placeCorner(place: StoragePlace, index: number): { x: number; y: number } {
  switch (index) {
    case 0:
      return { x: place.x, y: place.y };
    case 1:
      return { x: place.x + place.width, y: place.y };
    case 2:
      return { x: place.x + place.width, y: place.y + place.length };
    default:
      return { x: place.x, y: place.y + place.length };
  }
}

/**
 * Enables direct manipulation of package storage places on the map:
 * - click to select a place (syncs to the side panel selection)
 * - shift/ctrl-click to add or remove a place from the selection
 * - shift-drag a marquee box to select multiple places
 * - drag selected places to move them
 * - drag a corner handle of a single selected place to resize it
 *
 * Only active while a package storage area is being edited.
 */
export function usePlacesEditInteraction() {
  const packageStorageStore = useSelectedPackageStorageStore();
  const storageAreaStore = useSelectedStorageAreaStore();
  const shapeStore = useShapeStore();
  const selectedShapeStore = useSelectedShapeStore();

  const { selectedPlaceIds, operation } = storeToRefs(packageStorageStore);
  const { currentStorageArea } = storeToRefs(storageAreaStore);

  let map: OLMap | null = null;
  let element: HTMLElement | null = null;
  const handleSource = new VectorSource();
  let handleLayer: VectorLayerType<VectorSource> | null = null;
  const stopWatches: (() => void)[] = [];

  let dragging = false;
  let dragMoved = false;
  let dragStart: number[] | null = null;
  let dragIds: string[] = [];

  let resizing = false;
  let resizePlaceId: string | null = null;
  let resizeFixed: { x: number; y: number } | null = null;

  // Rubber-band (marquee) selection state.
  let marqueeActive = false;
  let marqueeStartPixel: number[] | null = null;
  let marqueeCurrentPixel: number[] | null = null;
  let marqueeEl: HTMLDivElement | null = null;

  // Snapshot captured at the start of a move/resize gesture, committed on drop.
  let pendingUndo: string | null = null;

  // Pointer position at press and whether it has traveled past the drag threshold.
  let downPixel: number[] | null = null;
  let pastThreshold = false;

  // Set after a marquee box selection so the trailing click does not override it.
  let suppressClick = false;

  // Pixel where the current press started, used to tell a map pan from a click
  // so panning the map does not clear the current place selection.
  let pressPixel: number[] | null = null;

  function editedAreaId(): string | undefined {
    if (operation.value === 'none') return undefined;
    return currentStorageArea.value?.id;
  }

  function basisForArea(areaId: string): ShapeBasis | null {
    const shape = shapeStore.getByStorageAreaId(areaId)[0];
    if (!shape?.shapeGeoJson) return null;
    return getShapeBasis(shape.shapeGeoJson);
  }

  /** Pushes the current storage area's bounds and z-index into the store. */
  function updateAreaBounds() {
    const areaId = editedAreaId();
    if (!areaId) return;
    const shape = shapeStore.getByStorageAreaId(areaId)[0];
    if (!shape?.shapeGeoJson) return;
    const basis = getShapeBasis(shape.shapeGeoJson);
    if (!basis) return;
    packageStorageStore.setAreaBounds(
      metersToMillimeters(basis.widthMeters),
      metersToMillimeters(basis.heightMeters),
    );
    // Use the live (in-edit) shape z-index so the minimum stays current.
    packageStorageStore.setAreaZIndex(selectedShapeStore.zIndex ?? shape.zIndex ?? 0);
  }

  function onShapeChanged() {
    updateAreaBounds();
    // Keep places inside the area when its shape is resized.
    packageStorageStore.clampAllToBounds();
    rebuildHandles();
  }

  function singleSelectedPlace(): StoragePlace | undefined {
    if (selectedPlaceIds.value.length !== 1) return undefined;
    return packageStorageStore.places.find((p) => p.id === selectedPlaceIds.value[0]);
  }

  function placeIdAtPixel(pixel: number[]): string | undefined {
    if (!map) return undefined;
    const areaId = editedAreaId();
    if (!areaId) return undefined;

    const features = map.getFeaturesAtPixel(pixel, { hitTolerance: 2 });
    for (const feature of features) {
      const placeId = feature.get('placeId') as string | undefined;
      const featureAreaId = feature.get('storageAreaId') as string | undefined;
      if (placeId && featureAreaId === areaId) return placeId;
    }
    return undefined;
  }

  function handleAtPixel(pixel: number[]): { placeId: string; corner: number } | undefined {
    if (!map || !handleLayer) return undefined;
    const features = map.getFeaturesAtPixel(pixel, {
      layerFilter: (layer) => layer === handleLayer,
      hitTolerance: 5,
    });
    const feature = features[0];
    if (!feature) return undefined;
    const placeId = feature.get('placeId') as string | undefined;
    const corner = feature.get('handleCorner') as number | undefined;
    if (placeId === undefined || corner === undefined) return undefined;
    return { placeId, corner };
  }

  function isAdditive(ev: MouseEvent): boolean {
    return ev.shiftKey || ev.ctrlKey || ev.metaKey;
  }

  function rebuildHandles() {
    handleSource.clear();

    const areaId = editedAreaId();
    if (!areaId) return;

    const place = singleSelectedPlace();
    if (!place) return;

    const basis = basisForArea(areaId);
    if (!basis) return;

    for (let corner = 0; corner < 4; corner++) {
      const local = placeCorner(place, corner);
      // Place corners are in millimeters; the geometry helper expects meters.
      const coordinate = geographicFromLocal(basis, millimetersToMeters(local.x), millimetersToMeters(local.y));
      const feature = new Feature({ geometry: new Point(coordinate) });
      feature.setProperties({ placeId: place.id, handleCorner: corner });
      handleSource.addFeature(feature);
    }
  }

  function setPanActive(active: boolean) {
    if (!map) return;
    const dragPan = getDragPan(map);
    if (dragPan) dragPan.setActive(active);
  }

  function setDragZoomActive(active: boolean) {
    if (!map) return;
    const dragZoom = getDragZoom(map);
    if (dragZoom) dragZoom.setActive(active);
  }

  function onMouseDown(ev: MouseEvent) {
    if (!map) return;
    if (ev.button !== 0) return;
    const areaId = editedAreaId();
    if (!areaId) return;

    // Interacting with the map should move keyboard focus off any panel input,
    // so shortcuts like Ctrl+Z (undo) reach the map handler afterwards.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // A fresh press starts a new selection interaction.
    suppressClick = false;

    // Record where the press started so onClick can distinguish a map pan
    // (pointer traveled) from a genuine click on empty space.
    pressPixel = map.getEventPixel(ev);

    // With a modifier held, start a rubber-band (marquee) selection.
    if (isAdditive(ev)) {
      marqueeActive = true;
      marqueeStartPixel = map.getEventPixel(ev);
      marqueeCurrentPixel = null;
      setPanActive(false);
      // Prevent OpenLayers shift-drag zoom from firing on mouseup while
      // marquee-selecting places with a modifier key.
      setDragZoomActive(false);
      ev.preventDefault();
      return;
    }

    const pixel = map.getEventPixel(ev);

    // Ensure bounds reflect the current area before any move/resize.
    updateAreaBounds();

    // Resize handles take priority over place selection/move.
    const handle = handleAtPixel(pixel);
    if (handle) {
      const place = packageStorageStore.places.find((p) => p.id === handle.placeId);
      if (place) {
        pendingUndo = packageStorageStore.snapshot();
        resizing = true;
        dragMoved = false;
        downPixel = pixel;
        pastThreshold = false;
        resizePlaceId = handle.placeId;
        resizeFixed = placeCorner(place, (handle.corner + 2) % 4);
        setPanActive(false);
        ev.preventDefault();
        return;
      }
    }

    const placeId = placeIdAtPixel(pixel);
    if (!placeId) return;

    if (!selectedPlaceIds.value.includes(placeId)) {
      packageStorageStore.selectPlace(placeId, false);
    }

    pendingUndo = packageStorageStore.snapshot();
    dragging = true;
    dragMoved = false;
    downPixel = pixel;
    pastThreshold = false;
    dragStart = getRenderedEventCoordinate(map, ev);
    dragIds = [...selectedPlaceIds.value];

    setPanActive(false);
    ev.preventDefault();
  }

  function onMouseMove(ev: MouseEvent) {
    if (!map) return;

    // Update the rubber-band box while dragging with a modifier held.
    if (marqueeActive && marqueeStartPixel) {
      marqueeCurrentPixel = map.getEventPixel(ev);
      updateMarqueeEl(pixelRect(marqueeStartPixel, marqueeCurrentPixel));
      return;
    }

    if (!dragging && !resizing) return;
    const areaId = editedAreaId();
    if (!areaId) return;

    // Ignore tiny movements so a click does not move or resize a place.
    if (!pastThreshold) {
      if (!downPixel) return;
      const pixel = map.getEventPixel(ev);
      const dx = pixel[0]! - downPixel[0]!;
      const dy = pixel[1]! - downPixel[1]!;
      if (Math.hypot(dx, dy) < dragThresholdPx) return;
      pastThreshold = true;
      // Re-baseline the move so it tracks the pointer from here without a jump.
      dragStart = getRenderedEventCoordinate(map, ev);
    }

    const basis = basisForArea(areaId);
    if (!basis) return;

    if (resizing && resizePlaceId && resizeFixed) {
      const coordinate = getRenderedEventCoordinate(map, ev);
      if (!coordinate) return;
      // resizeFixed is in millimeters; convert the pointer's local meters to mm.
      const local = localFromGeographic(basis, coordinate);
      const localX = metersToMillimeters(local.x);
      const localY = metersToMillimeters(local.y);
      const x = Math.min(resizeFixed.x, localX);
      const y = Math.min(resizeFixed.y, localY);
      const width = Math.max(minPlaceSize, Math.abs(resizeFixed.x - localX));
      const length = Math.max(minPlaceSize, Math.abs(resizeFixed.y - localY));
      packageStorageStore.updatePlace(resizePlaceId, {
        x: Math.max(0, x),
        y: Math.max(0, y),
        width,
        length,
      });
      dragMoved = true;
      return;
    }

    if (dragging && dragStart) {
      const coordinate = getRenderedEventCoordinate(map, ev);
      if (!coordinate) return;
      const deltaGeographic = [coordinate[0]! - dragStart[0]!, coordinate[1]! - dragStart[1]!];
      const delta = localDeltaFromGeographic(basis, deltaGeographic);
      // Move amounts are stored in millimeters.
      const deltaX = metersToMillimeters(delta.x);
      const deltaY = metersToMillimeters(delta.y);

      if (deltaX !== 0 || deltaY !== 0) {
        packageStorageStore.moveBy(dragIds, deltaX, deltaY);
        dragStart = coordinate;
        dragMoved = true;
      }
    }
  }

  function endDrag() {
    if (marqueeActive) {
      finishMarquee();
      return;
    }
    if (!dragging && !resizing) return;
    // Commit one undo restore point for the completed gesture (only if it moved).
    if (pendingUndo && dragMoved) {
      packageStorageStore.pushSnapshot(pendingUndo);
    }
    pendingUndo = null;
    dragging = false;
    dragStart = null;
    dragIds = [];
    downPixel = null;
    pastThreshold = false;
    resizing = false;
    resizePlaceId = null;
    resizeFixed = null;
    setPanActive(true);
    rebuildHandles();
  }

  function onClick(ev: MouseEvent) {
    if (!map) return;
    // A marquee box selection just completed; ignore the trailing click so it
    // does not collapse the selection to the place under the cursor.
    if (suppressClick) {
      suppressClick = false;
      return;
    }
    // Ignore the click that terminates a drag or resize.
    if (dragMoved) {
      dragMoved = false;
      return;
    }
    const areaId = editedAreaId();
    if (!areaId) return;

    const pixel = map.getEventPixel(ev);

    // If the pointer traveled since the press, this click ends a map pan rather
    // than selecting; keep the current selection instead of clearing it.
    if (pressPixel) {
      const dx = pixel[0]! - pressPixel[0]!;
      const dy = pixel[1]! - pressPixel[1]!;
      pressPixel = null;
      if (Math.hypot(dx, dy) >= dragThresholdPx) return;
    }

    if (handleAtPixel(pixel)) return;
    const placeId = placeIdAtPixel(pixel);

    if (isAdditive(ev)) {
      // Modifier click toggles a single place in the selection.
      if (placeId) packageStorageStore.selectPlace(placeId, true);
      return;
    }

    if (!placeId) {
      // Clicking empty space clears the selection.
      packageStorageStore.clearSelection();
    } else {
      packageStorageStore.selectPlace(placeId, false);
    }
  }

  // --- Rubber-band (marquee) selection -----------------------------------

  function pixelRect(a: number[], b: number[]): [number, number, number, number] {
    return [Math.min(a[0]!, b[0]!), Math.min(a[1]!, b[1]!), Math.max(a[0]!, b[0]!), Math.max(a[1]!, b[1]!)];
  }

  function ensureMarqueeEl(): HTMLDivElement | null {
    if (!map) return null;
    if (marqueeEl) return marqueeEl;
    const el = document.createElement('div');
    el.style.position = 'absolute';
    el.style.border = '1px solid #2563eb';
    el.style.background = 'rgba(37, 99, 235, 0.12)';
    el.style.pointerEvents = 'none';
    el.style.zIndex = '1000';
    el.style.display = 'none';
    map.getViewport().appendChild(el);
    marqueeEl = el;
    return el;
  }

  function updateMarqueeEl(rect: [number, number, number, number]) {
    const el = ensureMarqueeEl();
    if (!el) return;
    el.style.left = `${rect[0]}px`;
    el.style.top = `${rect[1]}px`;
    el.style.width = `${rect[2] - rect[0]}px`;
    el.style.height = `${rect[3] - rect[1]}px`;
    el.style.display = 'block';
  }

  function hideMarqueeEl() {
    if (marqueeEl) marqueeEl.style.display = 'none';
  }

  /** Collects the ids of places whose on-screen box intersects the pixel rect. */
  function placesInPixelRect(rect: [number, number, number, number]): string[] {
    if (!map) return [];
    const areaId = editedAreaId();
    if (!areaId) return [];

    const ids: string[] = [];
    const layers = map.getLayers().getArray();

    for (const layer of layers) {
      const source = (layer as { getSource?: () => unknown }).getSource?.();
      const vectorSource = source as { getFeatures?: () => unknown[] } | undefined;
      if (!vectorSource?.getFeatures) continue;

      for (const feature of vectorSource.getFeatures()) {
        const f = feature as {
          get: (key: string) => unknown;
          getGeometry?: () => { getExtent?: () => number[] } | undefined;
        };
        const placeId = f.get('placeId') as string | undefined;
        const featureAreaId = f.get('storageAreaId') as string | undefined;
        if (!placeId || featureAreaId !== areaId) continue;

        const geometry = f.getGeometry?.();
        const extent = geometry?.getExtent?.();
        if (!extent) continue;

        const p1 = map.getPixelFromCoordinate([extent[0]!, extent[1]!]);
        const p2 = map.getPixelFromCoordinate([extent[2]!, extent[3]!]);
        if (!p1 || !p2) continue;

        const fx0 = Math.min(p1[0]!, p2[0]!);
        const fy0 = Math.min(p1[1]!, p2[1]!);
        const fx1 = Math.max(p1[0]!, p2[0]!);
        const fy1 = Math.max(p1[1]!, p2[1]!);

        // Rectangle intersection test in pixel space.
        if (fx1 >= rect[0] && fx0 <= rect[2] && fy1 >= rect[1] && fy0 <= rect[3]) {
          ids.push(placeId);
        }
      }
    }

    return ids;
  }

  function finishMarquee() {
    const start = marqueeStartPixel;
    const current = marqueeCurrentPixel;
    marqueeActive = false;
    marqueeStartPixel = null;
    marqueeCurrentPixel = null;
    hideMarqueeEl();
    setPanActive(true);
    setDragZoomActive(true);

    // No drag (or a tiny one): treat as a modifier click and let onClick toggle.
    if (!start || !current) return;
    if (Math.hypot(current[0]! - start[0]!, current[1]! - start[1]!) < dragThresholdPx) return;

    const rect = pixelRect(start, current);
    const found = placesInPixelRect(rect);
    const union = new Set<string>([...selectedPlaceIds.value, ...found]);

    suppressClick = true;
    packageStorageStore.setSelection([...union]);
  }

  function attach(olMap: OLMap) {
    map = olMap;
    element = olMap.getTargetElement();

    handleLayer = new VectorLayer({ source: handleSource, style: handleStyle });
    handleLayer.setZIndex(20);
    olMap.addLayer(handleLayer);

    stopWatches.push(
      watch(
        () => editedAreaId(),
        () => {
          updateAreaBounds();
          // Snap any places that fell outside back inside when an area is opened.
          packageStorageStore.clampAllToBounds();
          rebuildHandles();
        },
      ),
    );
    stopWatches.push(watch(selectedPlaceIds, () => rebuildHandles()));
    stopWatches.push(watch(() => packageStorageStore.places, () => rebuildHandles(), { deep: true }));

    shapeStore.changed.on(onShapeChanged);

    element.addEventListener('mousedown', onMouseDown);
    element.addEventListener('mousemove', onMouseMove);
    element.addEventListener('click', onClick);
    window.addEventListener('mouseup', endDrag);

    updateAreaBounds();
    rebuildHandles();
  }

  function detach() {
    for (const stop of stopWatches) stop();
    stopWatches.length = 0;

    shapeStore.changed.off(onShapeChanged);

    if (map && handleLayer) {
      map.removeLayer(handleLayer);
    }
    if (element) {
      element.removeEventListener('mousedown', onMouseDown);
      element.removeEventListener('mousemove', onMouseMove);
      element.removeEventListener('click', onClick);
    }
    window.removeEventListener('mouseup', endDrag);
    if (marqueeEl) {
      marqueeEl.remove();
      marqueeEl = null;
    }
    // Ensure map zoom interaction is restored even if we detach mid-gesture.
    setDragZoomActive(true);
    handleSource.clear();
    map = null;
    element = null;
    handleLayer = null;
  }

  return { attach, detach };
}
